/**
 * 림월드식 차림(`Look`): 병종·영웅마다 머리·옷·갑옷·투구·무기·말. 층 그림은 `rim-manifest.ts`, 굽기는 `rim-bake.ts`.
 *
 * 병사는 병종이 차림을 정한다(나라·가지·등급). 장수·동료는 착장(`CombatantSpec.gear`, 화면 전용)이 정한다 — 장비 아이템 id가
 * 곧 층 id의 뒷부분이다(a_chalgap → armor_chalgap, w_hwandudo → weapon_hwandudo). 그림이 아직 없는 층은 같은 쪽의 가까운 층으로
 * 떨어진다(`resolve`) — Codex가 묶음마다 그림을 들이는 동안에도 게임이 늘 그린다.
 *
 * 렌더링 전용이다.
 */
import { Branch, BranchCode, Culture, Role, TROOP_BY_ID } from '../sim/troops.ts';
import type { CombatantSpec } from '../sim/troops.ts';
import type { Look, RimManifest } from './rim-bake.ts';

const C: Record<Culture, string> = { [Culture.Goguryeo]: 'go', [Culture.Baekje]: 'bj', [Culture.Silla]: 'sl' };

/** 병종별 갑옷·투구(가지·등급). 상징 병종은 `SIGNATURE`가 덮는다. */
const BY_BRANCH: Record<string, { armor?: string; helm?: string }> = {
  'levy:1': { helm: 'helm_band' },
  'spear:2': { armor: 'armor_pigap', helm: 'helm_gajuk' },
  'spear:3': { armor: 'armor_chalgap', helm: 'helm_iron' },
  'spear:4': { armor: 'armor_pangap', helm: 'helm_iron' },
  'blade:2': { armor: 'armor_pigap', helm: 'helm_band' },
  'blade:3': { armor: 'armor_chalgap', helm: 'helm_iron' },
  'blade:4': { armor: 'armor_pangap', helm: 'helm_jongjang' },
  'bow:2': { armor: 'armor_archer', helm: 'helm_band' },
  'bow:3': { armor: 'armor_archer', helm: 'helm_gajuk' },
  'bow:4': { armor: 'armor_archer', helm: 'helm_iron' },
  'horse:3': { armor: 'armor_chalgap', helm: 'helm_iron' },
  'horse:4': { armor: 'armor_pangap', helm: 'helm_jongjang' },
};

const SIGNATURE: Record<string, Partial<Look>> = {
  go_blade4: { armor: 'armor_joui', helm: undefined },
  go_horse4: { armor: 'armor_gaema', helm: 'helm_jongjang', horse: 'horse_gaema' },
  bj_blade4: { armor: 'armor_bang', helm: 'helm_plume' },
  bj_horse4: { horse: 'horse_gapju' },
  sl_spear4: { helm: 'helm_plume' },
  sl_blade4: { armor: 'armor_hwarang', helm: undefined },
  sl_horse4: { horse: 'horse_gapju' },
  bandit_thug: { helm: undefined },
  bandit_raider: { armor: 'armor_pigap', helm: undefined },
  bandit_archer: { armor: undefined, helm: undefined },
  // 탈영병·마적: 군에서 들고 나온 낡은 가죽 갑옷, 투구는 없다(도적의 머리가 보이게).
  bandit_deserter: { armor: 'armor_pigap', helm: undefined },
  bandit_rider: { armor: 'armor_pigap', helm: undefined, horse: 'horse_joranmal' },
};

/** 활 아이템의 그림 층. 이름이 층과 다른 것만 적는다(나머지는 `weapon_<id>`). 새 그림을 그리지 않고 병사의 활 층을 쓴다. */
const WEAPON_ART: Record<string, string> = {
  w_dangung: 'weapon_bow',
  w_gakgung: 'weapon_bow',
  w_maekgung: 'weapon_maekgung',
  w_no: 'weapon_crossbow',
  w_cheonbono: 'weapon_crossbow',
};

const MOUNT_ART: Record<string, string> = {
  m_joranmal: 'horse_joranmal',
  m_gunma: 'horse_gunma',
  m_deungja: 'horse_deungja',
  m_gapjuma: 'horse_gapju',
  m_gaema: 'horse_gaema',
  m_junma: 'horse_junma',
};

/** 영웅 머리: 장수·적장은 나라, 동료는 사람마다. */
const HERO_HEAD: Record<string, string> = {
  go_ondal: 'head_ondal', go_euldudji: 'head_euldu', go_myeongnim: 'head_myeongnim',
  bj_gyebaek: 'head_gyebaek', bj_seongchung: 'head_seongchung', bj_heukchi: 'head_heukchi',
  sl_jukjuk: 'head_jukjuk', sl_yusin: 'head_yusin', sl_alcheon: 'head_alcheon',
};

/** 병사·영웅 하나의 차림(그림이 있는지는 아직 보지 않는다 — `resolve`가 맞춘다). */
export function lookFor(spec: CombatantSpec): Look {
  const c = C[spec.culture];
  const mounted = spec.branch === BranchCode.Horse && spec.dismounted !== true;
  if (spec.role === Role.Soldier) {
    const t = TROOP_BY_ID.get(spec.troop);
    const bandit = spec.troop.startsWith('bandit');
    const branch = t?.branch ?? Branch.Levy;
    const base = BY_BRANCH[`${branch}:${Math.max(1, Math.min(4, t?.tier ?? 1))}`] ?? {};
    const look: Look = {
      head: bandit ? 'head_bd' : `head_${c}`,
      cloth: bandit ? 'cloth_bd' : `cloth_${c}`,
      armor: base.armor,
      helm: base.helm,
      weapon: `weapon_${t?.weapon ?? 'sword'}`,
      horse: mounted ? 'horse_gunma' : undefined,
    };
    const sig = SIGNATURE[spec.troop];
    if (sig) Object.assign(look, sig);
    if (!mounted) look.horse = undefined;
    return look;
  }
  const gear = spec.gear ?? {};
  const head =
    spec.role === Role.Leader
      ? `head_leader_${c}`
      : spec.role === Role.Companion
        ? (HERO_HEAD[spec.troop] ?? `head_${c}`)
        : spec.troop === 'bandit_chief'
          ? 'head_bdchief'
          : `head_chief_${c}`;
  return {
    head,
    cloth: `cloth_${c}`,
    armor: gear.armor ? `armor_${gear.armor.slice(2)}` : spec.role === Role.Chief ? 'armor_chalgap' : undefined,
    helm: gear.helm ? `helm_${gear.helm.slice(2)}` : undefined,
    weapon: gear.weapon ? (WEAPON_ART[gear.weapon] ?? `weapon_${gear.weapon.slice(2)}`) : 'weapon_sword',
    horse: mounted ? (gear.mount ? (MOUNT_ART[gear.mount] ?? 'horse_gunma') : 'horse_gunma') : undefined,
  };
}

/**
 * **적의 대장·부장의 차림**(`meta/foe-leaders.ts`의 인물마다). 플레이한 사람과 정했다(2026-10-07): 전장은 아이템 층을
 * 겹쳐 그리므로 새 그림을 통째로 그릴 일이 아니다 — 기존 부하장수 아홉은 적으로 설 때도 제 머리(`HERO_HEAD`)를 쓰고,
 * 스물넷 모두에게 인물에 맞는 갑옷·투구·무기·말을 있는 층에서 골라 준다. 그 전에는 대장과 부장이 모두 같은 적장의
 * 모습이었다(계백이 총대장으로 나와도 우리 부하장수 계백의 얼굴이 아니었다).
 *
 * 새 열다섯의 머리는 아직 없다 — `head_<이름>` 층이 시트에 들어오면 그것을 쓰고, 없으면 그 나라의 적장 머리다
 * (`foeLeaderLook`이 시트를 보고 고른다. `resolve`에 맡기면 병사의 머리로 떨어진다).
 *
 * 무기는 그림뿐이다 — 몸과 사거리는 모두 같은 적장의 것이다(`chiefSpec`). 화면 전용 표라 시뮬레이션은 모른다.
 * 고른 까닭이 있는 것만 적는다: 을지문덕·명림답부·김유신 같은 재상·대신은 관모, 연개소문은 개마와 큰 칼, 관창은
 * 화랑의 옷에 맨머리, 성충·을두지·김춘추 같은 문신은 갑옷 자리에 관복(`armor_gwanbok_*`)을 입는다 — 처음에는 갑옷
 * 없이 세웠더니 나라 공용 옷만 보여, 갑옷 입은 병사들 사이에서 차려입지 않은 맨몸처럼 눈에 띄었다(QA, 2026-10-07).
 *
 * 한 군대 안에서는 **투구와 말이 겹치지 않게** 벌려 둔다. 처음에는 철투구 + 군마가 한 나라에 셋씩이라(양만춘·강이식,
 * 의직·지수신, 김유신·김춘추의 관모 + 흰 말) 새 머리가 공용인 동안 멀리서 같은 사람으로 보였다(QA, 2026-10-07).
 * 열쇠가 다르다는 것만으로는 눈에 갈리지 않는다 — 시험이 투구·말 짝이 겹치지 않는지 본다.
 */
const FOE_LEADER_LOOK: Record<string, { head?: string; armor?: string; helm?: string; weapon: string; horse: string }> = {
  // 고구려
  go_eulji: { armor: 'armor_chalgap', helm: 'helm_jougwan', weapon: 'weapon_hwandudo', horse: 'horse_gaema' },
  go_yeon: { armor: 'armor_gaema', helm: 'helm_jongjang', weapon: 'weapon_yongbong', horse: 'horse_gapju' },
  go_ondal: { armor: 'armor_pigap', weapon: 'weapon_hwandudo', horse: 'horse_junma' },
  go_yang: { armor: 'armor_pangap', helm: 'helm_iron', weapon: 'weapon_cheoldo', horse: 'horse_gunma' },
  go_myeongnim: { armor: 'armor_chalgap', helm: 'helm_gwanmo', weapon: 'weapon_sword', horse: 'horse_gunma' },
  go_euldudji: { armor: 'armor_gwanbok_go', helm: 'helm_gwanmo', weapon: 'weapon_sword', horse: 'horse_joranmal' },
  go_gang: { armor: 'armor_eorin', helm: 'helm_balhyeong', weapon: 'weapon_lance', horse: 'horse_deungja' },
  go_goyeonsu: { armor: 'armor_pangap', helm: 'helm_plume', weapon: 'weapon_cheoldo', horse: 'horse_joranmal' },
  // 백제
  bj_gyebaek: { armor: 'armor_bang', helm: 'helm_plume', weapon: 'weapon_hwandudo', horse: 'horse_gapju' },
  bj_heukchi: { armor: 'armor_pangap', helm: 'helm_iron', weapon: 'weapon_cheoldo', horse: 'horse_gunma' },
  bj_seongchung: { armor: 'armor_gwanbok_bj', helm: 'helm_gwanmo', weapon: 'weapon_sword', horse: 'horse_joranmal' },
  bj_yunchung: { armor: 'armor_chalgap', helm: 'helm_jongjang', weapon: 'weapon_hwandudo', horse: 'horse_junma' },
  bj_uijik: { armor: 'armor_eorin', helm: 'helm_balhyeong', weapon: 'weapon_lance', horse: 'horse_gunma' },
  bj_eunsang: { armor: 'armor_chalgap', helm: 'helm_plume', weapon: 'weapon_cheoldo', horse: 'horse_deungja' },
  bj_boksin: { armor: 'armor_pigap', helm: 'helm_gwanmo', weapon: 'weapon_sword', horse: 'horse_gunma' },
  bj_jisusin: { armor: 'armor_pangap', helm: 'helm_band', weapon: 'weapon_spear', horse: 'horse_joranmal' },
  // 신라
  sl_yusin: { armor: 'armor_geumdong', helm: 'helm_gwanmo', weapon: 'weapon_yongbong', horse: 'horse_junma' },
  sl_alcheon: { armor: 'armor_chalgap', helm: 'helm_iron', weapon: 'weapon_hwandudo', horse: 'horse_gunma' },
  sl_heumsun: { armor: 'armor_pangap', helm: 'helm_cheonma', weapon: 'weapon_cheoldo', horse: 'horse_gapju' },
  sl_pumil: { armor: 'armor_eorin', helm: 'helm_plume', weapon: 'weapon_lance', horse: 'horse_deungja' },
  sl_jukjuk: { armor: 'armor_pigap', helm: 'helm_gajuk', weapon: 'weapon_spear', horse: 'horse_gunma' },
  sl_chunchu: { armor: 'armor_gwanbok_sl', helm: 'helm_gwanmo', weapon: 'weapon_sword', horse: 'horse_joranmal' },
  sl_gwanchang: { armor: 'armor_hwarang', weapon: 'weapon_lance', horse: 'horse_junma' },
  sl_binyeongja: { armor: 'armor_pigap', helm: 'helm_band', weapon: 'weapon_spear', horse: 'horse_gapju' },
};

/**
 * 적의 대장 `id`(`FoeLeader.id`)의 차림. 모르는 id면 null — 부르는 쪽이 여느 적장의 차림(`lookFor`)으로 그린다.
 * `mounted`: 산길에서 말을 내렸으면 false(`dismount`). 머리는 그 사람의 층이 시트에 있으면 그것, 없으면 그 나라의 적장 머리.
 */
export function foeLeaderLook(id: string, culture: Culture, mounted: boolean, man: RimManifest): Look | null {
  const row = FOE_LEADER_LOOK[id];
  if (!row) return null;
  const c = C[culture];
  // 부하장수와 같은 인물은 제 머리(`HERO_HEAD`), 새 인물은 `head_<id의 뒷부분>`(아직 없다).
  const own = HERO_HEAD[id] ?? `head_${id.slice(id.indexOf('_') + 1)}`;
  return {
    head: man.layers[own] !== undefined ? own : `head_chief_${c}`,
    cloth: `cloth_${c}`,
    armor: row.armor,
    helm: row.helm,
    weapon: row.weapon,
    horse: mounted ? row.horse : undefined,
  };
}

/** 차림을 정해 둔 적 대장의 id들(시험·검산용). */
export const FOE_LEADER_LOOK_IDS: readonly string[] = Object.keys(FOE_LEADER_LOOK);

/**
 * 그림이 없는 층을 가까운 층으로. 머리 → 그 나라 병사 머리 → 고구려 병사 머리, 옷 → 그 나라 민병 옷 → 없음,
 * 갑옷·투구 → 없음(맨몸·맨머리), 무기 → 같은 종류의 병사 무기 → 칼, 말 → 군마.
 */
export function resolve(look: Look, man: RimManifest): Look {
  const has = (id: string | undefined): id is string => id !== undefined && man.layers[id] !== undefined;
  const pick = (...ids: (string | undefined)[]): string | undefined => ids.find(has);
  const culture = look.cloth?.split('_')[1];
  return {
    head: pick(look.head, culture ? `head_${culture}` : undefined, 'head_go') ?? 'head_go',
    cloth: pick(look.cloth, culture ? `cloth_${culture}` : undefined),
    armor: pick(look.armor),
    helm: pick(look.helm),
    weapon: pick(look.weapon, 'weapon_sword') ?? 'weapon_sword',
    horse: look.horse ? pick(look.horse, 'horse_gunma') : undefined,
  };
}
